import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { Evaluation } from "@/types/evaluation";
import { useEvaluationStore } from "@/store/useEvaluationStore";
import { useTemplateStore } from "@/store/useTemplateStore";
import { parseTemplate } from "@/parser/templateParser";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { TemplatePicker } from "@/components/ui/TemplatePicker";
import { useToast } from "@/hooks/useToast";
import { InProgressCard, CompletedCard } from "@/components/filler/EvalCards";

function EmptyEvalIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="8"
        y="4"
        width="32"
        height="40"
        rx="3"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <line
        x1="14"
        y1="14"
        x2="34"
        y2="14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="14"
        y1="22"
        x2="30"
        y2="22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="14"
        y1="30"
        x2="26"
        y2="30"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EvalEmptyState({ onNew }: Readonly<{ onNew: () => void }>) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <EmptyEvalIcon />
      </div>
      <div className="empty-state-title">No evaluations yet</div>
      <div className="empty-state-description">
        Create your first evaluation to start filling out a clinical assessment using one of your
        templates.
      </div>
      <button className="primary" onClick={onNew}>
        + New Evaluation
      </button>
    </div>
  );
}

function InProgressSection({
  evaluations,
  onDelete,
  onNavigate,
}: Readonly<{
  evaluations: Evaluation[];
  onNavigate: (id: string) => void;
  onDelete: (target: { id: string; name: string }) => void;
}>) {
  if (evaluations.length === 0) {
    return null;
  }
  return (
    <div className="eval-list-section">
      <h3>In Progress</h3>
      <div className="eval-cards">
        {evaluations.map((ev) => (
          <InProgressCard key={ev.id} ev={ev} onNavigate={onNavigate} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function CompletedSection({
  evaluations,
  onDelete,
  onNavigate,
}: Readonly<{
  evaluations: Evaluation[];
  onNavigate: (id: string) => void;
  onDelete: (target: { id: string; name: string }) => void;
}>) {
  if (evaluations.length === 0) {
    return null;
  }
  return (
    <div className="eval-list-section">
      <h3>Completed</h3>
      <div className="eval-cards">
        {evaluations.map((ev) => (
          <CompletedCard key={ev.id} ev={ev} onNavigate={onNavigate} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function useEvalListStores() {
  const evaluations = useEvaluationStore((s) => s.evaluations);
  const createEval = useEvaluationStore((s) => s.create);
  const removeEval = useEvaluationStore((s) => s.remove);
  const templates = useTemplateStore((s) => s.templates);
  return { createEval, evaluations, removeEval, templates };
}

function useEvalListState() {
  const { createEval, evaluations, removeEval, templates } = useEvalListStores();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPicker, setShowPicker] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const allParsed = useMemo(
    () => templates.map((t) => ({ ...t, parsed: parseTemplate(t.source).content })),
    [templates],
  );

  const inProgress = evaluations.filter((e) => e.status === "in-progress");
  const completed = evaluations
    .filter((e) => e.status === "completed")
    .toSorted((a, b) => (b.completedAt ?? b.updatedAt) - (a.completedAt ?? a.updatedAt));

  const handleCreate = async (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) {
      return;
    }
    const evaluation = await createEval(template);
    toast("Evaluation created", "success");
    void navigate(`/unified/${evaluation.id}`);
    setShowPicker(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    await removeEval(deleteTarget.id);
    toast("Evaluation deleted", "success");
    setDeleteTarget(null);
  };

  const templateEntries = allParsed.map((t) => {
    const sectionCount = t.parsed.content.filter((n) => n.type === "section").length;
    return { category: t.parsed.category, id: t.id, name: t.parsed.name, sectionCount };
  });

  return {
    completed,
    deleteTarget,
    evaluations,
    handleCreate,
    handleDelete,
    inProgress,
    onNavigate: (id: string) => navigate(`/unified/${id}`),
    setDeleteTarget,
    setShowPicker,
    showPicker,
    templateEntries,
  };
}

function EvalListDialogs({
  deleteTarget,
  handleCreate,
  handleDelete,
  setDeleteTarget,
  setShowPicker,
  showPicker,
  templateEntries,
}: Readonly<{
  showPicker: boolean;
  setShowPicker: (v: boolean) => void;
  templateEntries: { id: string; name: string; category?: string; sectionCount: number }[];
  handleCreate: (id: string) => Promise<void>;
  deleteTarget: { id: string; name: string } | null;
  setDeleteTarget: (v: null) => void;
  handleDelete: () => Promise<void>;
}>) {
  return (
    <>
      {showPicker && (
        <TemplatePicker
          title="Choose a Template"
          templates={templateEntries}
          onSelect={(id) => handleCreate(id)}
          onClose={() => {
            setShowPicker(false);
          }}
        />
      )}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Evaluation"
        message={`Are you sure you want to delete "${deleteTarget?.name ?? ""}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteTarget(null);
        }}
      />
    </>
  );
}

export function EvaluationList() {
  const s = useEvalListState();

  const onDeleteTarget = (target: { id: string; name: string }) => {
    s.setDeleteTarget(target);
  };

  return (
    <div className="evaluation-list">
      <div className="eval-list-header">
        <h2>Evaluations</h2>
        <button
          className="primary"
          onClick={() => {
            s.setShowPicker(true);
          }}
        >
          + New Evaluation
        </button>
      </div>

      <EvalListDialogs
        showPicker={s.showPicker}
        setShowPicker={s.setShowPicker}
        templateEntries={s.templateEntries}
        handleCreate={s.handleCreate}
        deleteTarget={s.deleteTarget}
        setDeleteTarget={s.setDeleteTarget}
        handleDelete={s.handleDelete}
      />

      <InProgressSection
        evaluations={s.inProgress}
        onNavigate={s.onNavigate}
        onDelete={onDeleteTarget}
      />
      <CompletedSection
        evaluations={s.completed}
        onNavigate={s.onNavigate}
        onDelete={onDeleteTarget}
      />
      {s.evaluations.length === 0 && (
        <EvalEmptyState
          onNew={() => {
            s.setShowPicker(true);
          }}
        />
      )}
    </div>
  );
}
